import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface HeroImageProps {
  src: string;
  srcSet?: string;
  srcSetAvif?: string;
  srcSetWebp?: string;
  sizes?: string;
  alt: string;
  width: number;
  height: number;
  placeholder?: string;
  eager?: boolean;
  className?: string;
}

export function HeroImage({
  src,
  srcSet,
  srcSetAvif,
  srcSetWebp,
  sizes = "(min-width: 1280px) 1200px, (min-width: 768px) 90vw, 100vw",
  alt,
  width,
  height,
  placeholder,
  eager,
  className,
}: HeroImageProps) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete) {
      setLoaded(true);
    }
  }, []);

  return (
    <figure
      className={cn(
        "relative mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
        className
      )}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {placeholder ? (
        <div
          aria-hidden="true"
          className={cn(
            "absolute inset-0 z-0 bg-cover bg-center transition-opacity duration-700 ease-out",
            loaded ? "opacity-0" : "opacity-100"
          )}
          style={{
            backgroundImage: `url(${placeholder})`,
            filter: "blur(18px) scale(1.08)",
          }}
        />
      ) : null}
      <picture className={cn("relative z-10 block", loaded ? "opacity-100" : "opacity-0")}>
        {srcSetAvif ? (
          <source type="image/avif" srcSet={srcSetAvif} sizes={sizes} />
        ) : null}
        {srcSetWebp ? (
          <source type="image/webp" srcSet={srcSetWebp} sizes={sizes} />
        ) : null}
        <img
          ref={imgRef}
          src={src}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          width={width}
          height={height}
          loading={eager ? "eager" : "lazy"}
          decoding={eager ? "sync" : "async"}
          fetchPriority={eager ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
          className="h-auto w-full object-cover transition-opacity duration-700 ease-out"
        />
      </picture>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-background/40 via-transparent to-transparent"
      />
    </figure>
  );
}
